import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import type { Account } from "../../types";
import { api } from "../../lib/api";

export function useAccountOrder(accounts: Account[], onRefresh: () => Promise<unknown>) {
  const [preview, setPreview] = useState<Account[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [dragging, setDragging] = useState("");
  const [target, setTarget] = useState("");
  const gesture = useRef<{ id: string; group?: string; target: string; x: number; y: number; active: boolean; timer: ReturnType<typeof setTimeout> } | null>(null);
  const list = preview ?? accounts;

  function cancel() {
    if (gesture.current) clearTimeout(gesture.current.timer);
    gesture.current = null;
    setDragging("");
    setTarget("");
  }

  useEffect(() => () => { if (gesture.current) clearTimeout(gesture.current.timer); }, []);
  useEffect(() => {
    if (!dragging) return;
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") cancel(); };
    const scroll = setInterval(() => {
      const g = gesture.current;
      if (!g?.active) return;
      const edge = 85;
      const delta = g.y < edge ? -14 : g.y > window.innerHeight - edge ? 14 : 0;
      if (delta) window.scrollBy(0, delta);
      const card = document.elementFromPoint(g.x, g.y)?.closest<HTMLElement>("[data-order-id]");
      if (card?.dataset.orderGroup === g.group) {
        g.target = card?.dataset.orderId ?? g.id;
        setTarget(g.target);
      } else {
        g.target = g.id;
        setTarget("");
      }
    }, 40);
    window.addEventListener("keydown", escape);
    return () => { clearInterval(scroll); window.removeEventListener("keydown", escape); };
  }, [dragging]);

  async function move(id: string, to: string) {
    if (saving || id === to) return;
    const source = list.find(a => a.id === id);
    const destination = list.find(a => a.id === to);
    if (!source || !destination || source.owner !== destination.owner) return;
    const ordered = list.filter(a => a.owner === source.owner);
    const next = [...ordered];
    next.splice(ordered.findIndex(a => a.id === id), 1);
    next.splice(ordered.findIndex(a => a.id === to), 0, source);
    let index = 0;
    setPreview(list.map(a => a.owner === source.owner ? next[index++] : a));
    setSaving(true);
    setMessage("Saving account order…");
    try {
      await api("settings/accounts/order", { accountIds: next.map(a => a.id), expectedIds: ordered.map(a => a.id) });
      if (!await onRefresh()) throw new Error("Order saved, but the account list could not refresh.");
      setMessage("Account order saved.");
    } catch (error) {
      // Resolve an uncertain response from the authoritative read; never silently claim a save.
      try { await onRefresh(); } catch { /* Keep the error visible; a reload can resolve it. */ }
      setMessage(`${error instanceof Error ? error.message : "Could not save account order."} Refresh to check the saved order before retrying.`);
    } finally {
      setPreview(null);
      setSaving(false);
    }
  }

  function start(event: PointerEvent, id: string) {
    if (saving || event.button !== 0) return;
    event.preventDefault();
    cancel();
    const element = event.currentTarget;
    element.setPointerCapture(event.pointerId);
    const g = { id, group: element.closest<HTMLElement>("[data-order-group]")?.dataset.orderGroup, target: id, x: event.clientX, y: event.clientY, active: false, timer: setTimeout(() => {
      g.active = true;
      setDragging(id);
      setMessage("Drag to move. Escape cancels.");
    }, event.pointerType === "mouse" ? 0 : 300) };
    gesture.current = g;
  }

  function update(event: PointerEvent) {
    const g = gesture.current;
    if (!g) return;
    if (!g.active && Math.hypot(g.x - event.clientX, g.y - event.clientY) > 12) { cancel(); return; }
    g.x = event.clientX;
    g.y = event.clientY;
    if (g.active) event.preventDefault();
  }

  function finish() {
    const g = gesture.current;
    cancel();
    if (g?.active) void move(g.id, g.target);
  }

  return { accounts: list, saving, message, dragging, target, move, start, update, finish, cancel };
}
